import React, { useContext, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import {
  ClientStateReducerProviderToken,
  type ApplicationClientActionType,
  type ApplicationClientStateType,
  type IClientStateReducerProvider,
} from 'src/services/ClientStateReducerProvider';
import { useImmerReducer } from 'use-immer';
import { ServicesProvider } from '../services/ServicesProvider';
import { useApplicationServerContext } from './server';
export type { ApplicationClientActionType, ApplicationClientStateType };

// Initial client state (if required)
const initialClientState: ApplicationClientStateType = {
  currentLoginType: null,
  isSubmitting: false,
  isSubmittingCancel: false,
  showMarketingContent: false,
  isSubmittingTwoStepLogin: false,
  form: {
    username: { value: '', validationState: 'unset' },
    password: { value: '', validationState: 'unset' },
    phonenumber: { value: '', validationState: 'unset' },
    authenticationNumber: { value: '', validationState: 'unset' },
    kennitala: { value: '', validationState: 'unset' },
    code: { value: '', validationState: 'unset' },
    xsrf: '',
  },
  isLandsbankinnApp: false,
};

// The React Context type used in the application.
type ApplicationClientContextType = [
  ApplicationClientStateType,
  React.Dispatch<ApplicationClientActionType>,
];

/**
 * The React Context object that components subscribe to.
 * The default value for the context is the previously defined initial state.
 * See https://reactjs.org/docs/context.html#reactcreatecontext for more documentation.
 */
export const ApplicationClientContext =
  React.createContext<ApplicationClientContextType>([
    initialClientState,
    () => {},
  ]);

/**
 * A hook that returns the application context.
 * @returns The application context, current state and dispatch function
 */
export function useApplicationClientContext(): ApplicationClientContextType {
  return useContext(ApplicationClientContext);
}

/**
 * Component that allows consuming components to subscribe to context changes.
 * @param props Optional children components
 * @returns Application context provider
 */
export function ApplicationClientContextProvider(props: {
  initialState?: ApplicationClientStateType;
  children?: ReactNode;
}) {
  // use the server context that is getting the initial state from the server
  const [serverState] = useApplicationServerContext();
  const { search } = useLocation();
  const urlSearchParam = new URLSearchParams(search);
  const returnUrl = urlSearchParam.get('returnUrl');

  // Copy error server state to client state, because we want to clear the error state when switching login types
  const init: ApplicationClientStateType = useMemo(
    () => ({
      ...(props.initialState || initialClientState),
      errors: serverState.errors,
      isDevice: returnUrl?.startsWith('landsbankinn') === true,
    }),
    [props.initialState, serverState.errors, returnUrl],
  );

  const clientStateReducerProvider = useMemo(() => {
    return ServicesProvider.getSingleton<IClientStateReducerProvider>(
      ClientStateReducerProviderToken,
      init,
    );
  }, [init]);

  const [state, dispatch] = useImmerReducer(
    clientStateReducerProvider.reducer,
    init,
  );
  return (
    <ApplicationClientContext.Provider value={[state, dispatch]}>
      {props.children}
    </ApplicationClientContext.Provider>
  );
}
